/**
 * Reusable Stat Card Component
 * Used across all dashboards for displaying statistics
 */

import React from 'react';
import { COLORS, SPACING, TYPOGRAPHY, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

export interface StatCardProps {
  title: string;
  value: number | string | React.ReactNode;
  icon: string | React.ReactNode;
  color: string;        // background color of icon box
  iconColor?: string;   // stroke/fill color for SVG icons
  subtitle?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  onClick?: () => void;
}

export const StatCard = React.memo<StatCardProps>(({
  title,
  value,
  icon,
  color,
  iconColor,
  subtitle,
  trend,
  onClick
}) => {
  const isClickable = !!onClick;

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: COLORS.background.primary,
        border: `1px solid ${COLORS.border.light}`,
        borderRadius: BORDER_RADIUS.xl,
        padding: SPACING.xl,
        boxShadow: SHADOWS.sm,
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (isClickable) {
          e.currentTarget.style.boxShadow = SHADOWS.md;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (isClickable) {
          e.currentTarget.style.boxShadow = SHADOWS.sm;
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: TYPOGRAPHY.fontSize.sm,
            color: COLORS.text.secondary,
            fontWeight: TYPOGRAPHY.fontWeight.medium,
            marginBottom: SPACING.sm,
          }}>
            {title}
          </div>

          <div style={{
            fontSize: TYPOGRAPHY.fontSize['3xl'],
            fontWeight: TYPOGRAPHY.fontWeight.bold,
            color: COLORS.text.primary,
            marginBottom: subtitle || trend ? SPACING.sm : 0,
          }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>

          {subtitle && (
            <div style={{
              fontSize: TYPOGRAPHY.fontSize.xs,
              color: COLORS.text.tertiary,
            }}>
              {subtitle}
            </div>
          )}

          {trend && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: SPACING.xs,
              fontSize: TYPOGRAPHY.fontSize.sm,
              color: trend.direction === 'up' ? COLORS.success.main : COLORS.danger.main,
              fontWeight: TYPOGRAPHY.fontWeight.medium,
            }}>
              <span>{trend.direction === 'up' ? '↑' : '↓'}</span>
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>

        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: BORDER_RADIUS.lg,
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor ?? COLORS.text.secondary,
        }}>
          {typeof icon === 'string' ? (
            <span style={{ fontSize: TYPOGRAPHY.fontSize['2xl'] }}>{icon}</span>
          ) : (
            <div style={{ display: 'flex', color: 'inherit' }}>{icon}</div>
          )}
        </div>
      </div>
    </div>
  );
});

StatCard.displayName = 'StatCard';
